import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Calendar, Lock, Plus, Trash2, Edit2, Save, X, 
  Copy, Check, Info, List, HelpCircle, Globe, Settings,
  Briefcase, User, Scale, Building2, UserX
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

// Case-specific field definitions organized by category
const CASE_SPECIFIC_FIELDS = {
  case: {
    icon: Briefcase,
    color: 'blue',
    title: 'Case Fields',
    description: 'Use these placeholders to auto-fill case information from your CRM',
    fields: [
      { field_id: 'case_title', placeholder: '{{case.case_title}}', label: 'Case Title', description: 'The full title of the case (e.g., "In re Marriage of Doe")' },
      { field_id: 'case_court_case_number', placeholder: '{{case.court_case_number}}', label: 'Court Case Number', description: 'Official court-assigned case number' },
      { field_id: 'case_internal_case_number', placeholder: '{{case.internal_case_number}}', label: 'Internal Case Number', description: 'Your firm\'s internal case reference number' },
      { field_id: 'case_status', placeholder: '{{case.status}}', label: 'Case Status', description: 'Current status (Active, Pending, Closed, On Hold)' },
      { field_id: 'case_description', placeholder: '{{case.description}}', label: 'Case Description', description: 'Brief description of the case' },
      { field_id: 'case_petitioner_name', placeholder: '{{case.petitioner_name}}', label: 'Petitioner Name', description: 'Name of the petitioner/plaintiff' },
      { field_id: 'case_respondent_name', placeholder: '{{case.respondent_name}}', label: 'Respondent Name', description: 'Name of the respondent/defendant' },
      { field_id: 'case_opposing_party_name', placeholder: '{{case.opposing_party_name}}', label: 'Opposing Party Name', description: 'Name of the opposing party' },
      { field_id: 'case_opposing_party_addr1', placeholder: '{{case.opposing_party_address_line_1}}', label: 'Opposing Party Address Line 1', description: 'Street address of opposing party' },
      { field_id: 'case_opposing_party_addr2', placeholder: '{{case.opposing_party_address_line_2}}', label: 'Opposing Party Address Line 2', description: 'Apartment, suite, etc. of opposing party' },
      { field_id: 'case_opposing_party_city', placeholder: '{{case.opposing_party_city}}', label: 'Opposing Party City', description: 'City of opposing party' },
      { field_id: 'case_opposing_party_state', placeholder: '{{case.opposing_party_state}}', label: 'Opposing Party State', description: 'State of opposing party' },
      { field_id: 'case_opposing_party_zip', placeholder: '{{case.opposing_party_zip}}', label: 'Opposing Party ZIP', description: 'ZIP code of opposing party' },
      { field_id: 'case_filing_date', placeholder: '{{case.filing_date}}', label: 'Filing Date', description: 'Date the case was filed' },
      { field_id: 'case_next_hearing_date', placeholder: '{{case.next_hearing_date}}', label: 'Next Hearing Date', description: 'Date of next scheduled hearing' },
      { field_id: 'case_trial_date', placeholder: '{{case.trial_date}}', label: 'Trial Date', description: 'Scheduled trial date' },
      { field_id: 'case_court_name', placeholder: '{{case.court_name}}', label: 'Court Name', description: 'Name of the court (e.g., "Superior Court of California")' },
      { field_id: 'case_county', placeholder: '{{case.county}}', label: 'County', description: 'County where the case is filed' },
      { field_id: 'case_court_branch', placeholder: '{{case.court_branch}}', label: 'Court Branch', description: 'Specific court branch or department' },
      { field_id: 'case_judge_name', placeholder: '{{case.judge_name}}', label: 'Judge Name', description: 'Assigned judge\'s name' },
      { field_id: 'case_date_of_marriage', placeholder: '{{case.date_of_marriage}}', label: 'Date of Marriage', description: 'Date when the parties were married' },
      { field_id: 'case_date_of_separation', placeholder: '{{case.date_of_separation}}', label: 'Date of Separation', description: 'Date when the parties separated' },
      { field_id: 'case_marriage_duration_years', placeholder: '{{case.marriage_duration.years}}', label: 'Marriage Duration (Years)', description: 'Number of years of marriage (calculated from marriage to separation date)' },
      { field_id: 'case_marriage_duration_months', placeholder: '{{case.marriage_duration.months}}', label: 'Marriage Duration (Months)', description: 'Additional months of marriage beyond complete years' },
      { field_id: 'protected_person_name', placeholder: '{{case.protected_person_name}}', label: 'Protected Person Name', description: 'Name of the protected person in DVRO/TRO' },
      { field_id: 'protected_person_street', placeholder: '{{case.protected_person_street}}', label: 'Protected Person Street', description: 'Street address of protected person' },
      { field_id: 'protected_person_city', placeholder: '{{case.protected_person_city}}', label: 'Protected Person City', description: 'City of protected person' },
      { field_id: 'protected_person_state', placeholder: '{{case.protected_person_state}}', label: 'Protected Person State', description: 'State of protected person' },
      { field_id: 'protected_person_zip', placeholder: '{{case.protected_person_zip}}', label: 'Protected Person ZIP', description: 'ZIP code of protected person' },
      { field_id: 'protected_person_phone', placeholder: '{{case.protected_person_phone}}', label: 'Protected Person Phone', description: 'Phone number of protected person' },
      { field_id: 'restrained_person_name', placeholder: '{{case.restrained_person_name}}', label: 'Restrained Person Name', description: 'Name of the restrained person in DVRO/TRO' },
      { field_id: 'restrained_person_sex', placeholder: '{{case.restrained_person_sex}}', label: 'Restrained Person Sex', description: 'Sex of restrained person (M/F)' },
      { field_id: 'restrained_person_height', placeholder: '{{case.restrained_person_height}}', label: 'Restrained Person Height', description: 'Height of restrained person' },
      { field_id: 'restrained_person_weight', placeholder: '{{case.restrained_person_weight}}', label: 'Restrained Person Weight', description: 'Weight of restrained person' },
      { field_id: 'restrained_person_race', placeholder: '{{case.restrained_person_race}}', label: 'Restrained Person Race', description: 'Race of restrained person' },
      { field_id: 'restrained_person_hair_color', placeholder: '{{case.restrained_person_hair_color}}', label: 'Restrained Person Hair Color', description: 'Hair color of restrained person' },
      { field_id: 'restrained_person_eye_color', placeholder: '{{case.restrained_person_eye_color}}', label: 'Restrained Person Eye Color', description: 'Eye color of restrained person' },
      { field_id: 'restrained_person_age', placeholder: '{{case.restrained_person_age}}', label: 'Restrained Person Age', description: 'Age of restrained person' },
      { field_id: 'restrained_person_dob', placeholder: '{{case.restrained_person_dob}}', label: 'Restrained Person Date of Birth', description: 'Date of birth of restrained person' },
    ]
  },
  client: {
    icon: User,
    color: 'green',
    title: 'Client Fields',
    description: 'Use these placeholders to auto-fill client information',
    fields: [
      { field_id: 'client_full_name', placeholder: '{{client.full_name}}', label: 'Client Full Name', description: 'Client\'s complete legal name' },
      { field_id: 'client_email', placeholder: '{{client.email}}', label: 'Client Email', description: 'Client\'s email address' },
      { field_id: 'client_phone', placeholder: '{{client.phone}}', label: 'Client Phone', description: 'Client\'s phone number' },
      { field_id: 'client_dob', placeholder: '{{client.date_of_birth}}', label: 'Client Date of Birth', description: 'Client\'s date of birth' },
      { field_id: 'client_addr1', placeholder: '{{client.address.address_line_1}}', label: 'Client Address Line 1', description: 'Street address' },
      { field_id: 'client_addr2', placeholder: '{{client.address.address_line_2}}', label: 'Client Address Line 2', description: 'Apartment, suite, unit, etc.' },
      { field_id: 'client_city', placeholder: '{{client.address.city}}', label: 'Client City', description: 'City' },
      { field_id: 'client_state', placeholder: '{{client.address.state}}', label: 'Client State', description: 'State' },
      { field_id: 'client_zip', placeholder: '{{client.address.zip}}', label: 'Client ZIP', description: 'ZIP/Postal code' },
      { field_id: 'client_has_many_children', placeholder: '{{client.has_many_children}}', label: 'Has More Than 3 Children', description: '"On" if client has more than 3 children, "Off" otherwise' },
      { field_id: 'child_0_full_name', placeholder: '{{child[0].full_name}}', label: 'First Child Name', description: 'Full name of the first child' },
      { field_id: 'child_0_dob', placeholder: '{{child[0].date_of_birth}}', label: 'First Child Date of Birth', description: 'Date of birth of the first child' },
      { field_id: 'child_0_age_years', placeholder: '{{child[0].age.years}}', label: 'First Child Age (Years)', description: 'Age in years of the first child (calculated from date of birth)' },
      { field_id: 'child_0_age_months', placeholder: '{{child[0].age.months}}', label: 'First Child Age (Months)', description: 'Additional months beyond complete years for first child' },
      { field_id: 'child_0_addr1', placeholder: '{{child[0].address.address_line_1}}', label: 'First Child Address Line 1', description: 'Street address of first child' },
      { field_id: 'child_0_addr2', placeholder: '{{child[0].address.address_line_2}}', label: 'First Child Address Line 2', description: 'Apartment, suite, etc. for first child' },
      { field_id: 'child_0_city', placeholder: '{{child[0].address.city}}', label: 'First Child City', description: 'City of first child' },
      { field_id: 'child_0_state', placeholder: '{{child[0].address.state}}', label: 'First Child State', description: 'State of first child' },
      { field_id: 'child_0_zip', placeholder: '{{child[0].address.zip}}', label: 'First Child ZIP', description: 'ZIP code of first child' },
      { field_id: 'child_1_full_name', placeholder: '{{child[1].full_name}}', label: 'Second Child Name', description: 'Full name of the second child' },
      { field_id: 'child_1_dob', placeholder: '{{child[1].date_of_birth}}', label: 'Second Child Date of Birth', description: 'Date of birth of the second child' },
      { field_id: 'child_1_age_years', placeholder: '{{child[1].age.years}}', label: 'Second Child Age (Years)', description: 'Age in years of the second child (calculated from date of birth)' },
      { field_id: 'child_1_age_months', placeholder: '{{child[1].age.months}}', label: 'Second Child Age (Months)', description: 'Additional months beyond complete years for second child' },
      { field_id: 'child_1_addr1', placeholder: '{{child[1].address.address_line_1}}', label: 'Second Child Address Line 1', description: 'Street address of second child' },
      { field_id: 'child_1_addr2', placeholder: '{{child[1].address.address_line_2}}', label: 'Second Child Address Line 2', description: 'Apartment, suite, etc. for second child' },
      { field_id: 'child_1_city', placeholder: '{{child[1].address.city}}', label: 'Second Child City', description: 'City of second child' },
      { field_id: 'child_1_state', placeholder: '{{child[1].address.state}}', label: 'Second Child State', description: 'State of second child' },
      { field_id: 'child_1_zip', placeholder: '{{child[1].address.zip}}', label: 'Second Child ZIP', description: 'ZIP code of second child' },
      { field_id: 'child_2_full_name', placeholder: '{{child[2].full_name}}', label: 'Third Child Name', description: 'Full name of the third child' },
      { field_id: 'child_2_dob', placeholder: '{{child[2].date_of_birth}}', label: 'Third Child Date of Birth', description: 'Date of birth of the third child' },
      { field_id: 'child_2_age_years', placeholder: '{{child[2].age.years}}', label: 'Third Child Age (Years)', description: 'Age in years of the third child (calculated from date of birth)' },
      { field_id: 'child_2_age_months', placeholder: '{{child[2].age.months}}', label: 'Third Child Age (Months)', description: 'Additional months beyond complete years for third child' },
      { field_id: 'child_2_addr1', placeholder: '{{child[2].address.address_line_1}}', label: 'Third Child Address Line 1', description: 'Street address of third child' },
      { field_id: 'child_2_addr2', placeholder: '{{child[2].address.address_line_2}}', label: 'Third Child Address Line 2', description: 'Apartment, suite, etc. for third child' },
      { field_id: 'child_2_city', placeholder: '{{child[2].address.city}}', label: 'Third Child City', description: 'City of third child' },
      { field_id: 'child_2_state', placeholder: '{{child[2].address.state}}', label: 'Third Child State', description: 'State of third child' },
      { field_id: 'child_2_zip', placeholder: '{{child[2].address.zip}}', label: 'Third Child ZIP', description: 'ZIP code of third child' },
    ]
  },
  attorney: {
    icon: Scale,
    color: 'purple',
    title: 'Attorney Fields',
    description: 'Use these placeholders to auto-fill attorney information',
    fields: [
      { field_id: 'atty_full_name', placeholder: '{{attorney.full_name}}', label: 'Attorney Full Name', description: 'Attorney\'s complete name' },
      { field_id: 'atty_email', placeholder: '{{attorney.email}}', label: 'Attorney Email', description: 'Attorney\'s email address' },
      { field_id: 'atty_phone', placeholder: '{{attorney.phone}}', label: 'Attorney Phone', description: 'Attorney\'s phone number' },
      { field_id: 'atty_bar', placeholder: '{{attorney.bar_number}}', label: 'Attorney Bar Number', description: 'State bar license number' },
      { field_id: 'atty_authority', placeholder: '{{attorney.licensing_authority}}', label: 'Licensing Authority', description: 'Bar association (e.g., "California State Bar")' },
    ]
  },
  firm: {
    icon: Building2,
    color: 'orange',
    title: 'Firm Fields',
    description: 'Use these placeholders to auto-fill your law firm information',
    fields: [
      { field_id: 'firm_name', placeholder: '{{firm.firm_name}}', label: 'Firm Name', description: 'Name of your law firm' },
      { field_id: 'firm_phone', placeholder: '{{firm.phone}}', label: 'Firm Phone', description: 'Firm\'s main phone number' },
      { field_id: 'firm_fax', placeholder: '{{firm.fax}}', label: 'Firm Fax', description: 'Firm\'s fax number' },

      { field_id: 'firm_email', placeholder: '{{firm.email}}', label: 'Firm Email', description: 'Firm\'s main email address' },
      { field_id: 'firm_website', placeholder: '{{firm.website}}', label: 'Firm Website', description: 'Firm\'s website URL' },
      { field_id: 'firm_addr1', placeholder: '{{firm.address.address_line_1}}', label: 'Firm Address Line 1', description: 'Street address' },
      { field_id: 'firm_addr2', placeholder: '{{firm.address.address_line_2}}', label: 'Firm Address Line 2', description: 'Suite, floor, etc.' },
      { field_id: 'firm_city', placeholder: '{{firm.address.city}}', label: 'Firm City', description: 'City' },
      { field_id: 'firm_state', placeholder: '{{firm.address.state}}', label: 'Firm State', description: 'State' },
      { field_id: 'firm_zip', placeholder: '{{firm.address.zip}}', label: 'Firm ZIP', description: 'ZIP/Postal code' },
    ]
  },
  court: {
    icon: Scale,
    color: 'indigo',
    title: 'Court Fields',
    description: 'Use these placeholders to auto-fill court information',
    fields: [
      { field_id: 'court_name', placeholder: '{{court.court_name}}', label: 'Court Name', description: 'Full name of the court' },
      { field_id: 'court_county', placeholder: '{{court.county}}', label: 'County', description: 'County where court is located' },
      { field_id: 'court_branch', placeholder: '{{court.branch}}', label: 'Branch/Department', description: 'Court branch or department' },
      { field_id: 'court_street', placeholder: '{{court.street_address}}', label: 'Street Address', description: 'Court street address' },
      { field_id: 'court_mailing', placeholder: '{{court.mailing_address}}', label: 'Mailing Address', description: 'Court mailing address' },
      { field_id: 'court_city', placeholder: '{{court.city}}', label: 'City', description: 'Court city' },
      { field_id: 'court_state', placeholder: '{{court.state}}', label: 'State', description: 'Court state' },
      { field_id: 'court_zip', placeholder: '{{court.zip}}', label: 'ZIP', description: 'Court ZIP code' },
    ]
  },
  opposing_counsel: {
    icon: UserX,
    color: 'red',
    title: 'Opposing Counsel Fields',
    description: 'Use these placeholders to auto-fill opposing counsel information',
    fields: [
      { field_id: 'opp_first_name', placeholder: '{{opposing_counsel.first_name}}', label: 'First Name', description: 'Opposing counsel\'s first name' },
      { field_id: 'opp_last_name', placeholder: '{{opposing_counsel.last_name}}', label: 'Last Name', description: 'Opposing counsel\'s last name' },
      { field_id: 'opp_full_name', placeholder: '{{opposing_counsel.full_name}}', label: 'Full Name', description: 'Opposing counsel\'s complete name' },
      { field_id: 'opp_firm', placeholder: '{{opposing_counsel.law_firm}}', label: 'Law Firm', description: 'Opposing counsel\'s law firm name' },
      { field_id: 'opp_email', placeholder: '{{opposing_counsel.email}}', label: 'Email', description: 'Opposing counsel\'s email' },
      { field_id: 'opp_phone', placeholder: '{{opposing_counsel.phone}}', label: 'Phone', description: 'Opposing counsel\'s phone number' },
      { field_id: 'opp_addr1', placeholder: '{{opposing_counsel.address.address_line_1}}', label: 'Address Line 1', description: 'Street address' },
      { field_id: 'opp_addr2', placeholder: '{{opposing_counsel.address.address_line_2}}', label: 'Address Line 2', description: 'Suite, floor, etc.' },
      { field_id: 'opp_city', placeholder: '{{opposing_counsel.address.city}}', label: 'City', description: 'City' },
      { field_id: 'opp_state', placeholder: '{{opposing_counsel.address.state}}', label: 'State', description: 'State' },
      { field_id: 'opp_zip', placeholder: '{{opposing_counsel.address.zip}}', label: 'ZIP', description: 'ZIP/Postal code' },
    ]
  }
};

// Helper function to provide example values for case fields
const getExampleValue = (fieldId) => {
  const examples = {
    // Case fields
    case_title: 'In re Marriage of Smith',
    case_court_case_number: '2025-FAM-001234',
    case_internal_case_number: 'SMITH-001',
    case_status: 'Active',
    case_description: 'Family law dissolution',
    case_petitioner_name: 'Jane Smith',
    case_respondent_name: 'John Smith',
    case_opposing_party_name: 'John Smith',
    case_opposing_party_addr1: '456 Oak Street',
    case_opposing_party_addr2: 'Unit 2',
    case_opposing_party_city: 'Santa Monica',
    case_opposing_party_state: 'CA',
    case_opposing_party_zip: '90401',
    case_filing_date: '01/15/2025',
    case_next_hearing_date: '02/28/2025',
    case_trial_date: '06/15/2025',
    case_court_name: 'Superior Court of California',
    case_county: 'Los Angeles',
    case_court_branch: 'Department 12',
    case_judge_name: 'Hon. Sarah Johnson',
    case_date_of_marriage: '06/15/2010',
    case_date_of_separation: '03/20/2024',
    case_marriage_duration_years: '13',
    case_marriage_duration_months: '9',
    protected_person_name: 'Jane Smith',
    protected_person_street: '123 Safe Haven Rd',
    protected_person_city: 'Los Angeles',
    protected_person_state: 'CA',
    protected_person_zip: '90001',
    protected_person_phone: '(555) 123-4567',
    restrained_person_name: 'John Doe',
    restrained_person_sex: 'M',
    restrained_person_height: '6\'0"',
    restrained_person_weight: '180 lbs',
    restrained_person_race: 'Caucasian',
    restrained_person_hair_color: 'Brown',
    restrained_person_eye_color: 'Blue',
    restrained_person_age: '35',
    restrained_person_dob: '01/15/1990',
    // Client fields
    client_full_name: 'Jane Smith',
    client_email: 'jane.smith@email.com',
    client_phone: '(555) 123-4567',
    client_dob: '03/15/1985',
    client_addr1: '123 Main Street',
    client_addr2: 'Apt 4B',
    client_city: 'Los Angeles',
    client_state: 'CA',
    client_zip: '90001',
    client_has_many_children: 'Off',
    child_0_full_name: 'Emily Smith',
    child_0_dob: '04/12/2016',
    child_0_age_years: '8',
    child_0_age_months: '3',
    child_0_addr1: '123 Main Street',
    child_0_addr2: '',
    child_0_city: 'Los Angeles',
    child_0_state: 'CA',
    child_0_zip: '90001',
    child_1_full_name: 'Michael Smith',
    child_1_dob: '09/22/2018',
    child_1_age_years: '5',
    child_1_age_months: '7',
    child_1_addr1: '123 Main Street',
    child_1_addr2: '',
    child_1_city: 'Los Angeles',
    child_1_state: 'CA',
    child_1_zip: '90001',
    child_2_full_name: 'Sarah Smith',
    child_2_dob: '01/08/2021',
    child_2_age_years: '3',
    child_2_age_months: '1',
    child_2_addr1: '456 Oak Avenue',
    child_2_addr2: 'Apt 2B',
    child_2_city: 'Santa Monica',
    child_2_state: 'CA',
    child_2_zip: '90401',
    // Attorney fields
    atty_full_name: 'Robert Williams, Esq.',
    atty_email: 'rwilliams@lawfirm.com',
    atty_phone: '(555) 987-6543',
    atty_bar: 'CA12345',
    atty_authority: 'California State Bar',
    // Firm fields
    firm_name: 'Williams & Associates',
    firm_phone: '(555) 555-5555',
    firm_email: 'contact@williamslaw.com',
    firm_website: 'www.williamslaw.com',
    firm_addr1: '500 Corporate Drive',
    firm_addr2: 'Suite 400',
    firm_city: 'Los Angeles',
    firm_state: 'CA',
    firm_zip: '90010',
    // Opposing counsel fields
    opp_first_name: 'Michael',
    opp_last_name: 'Johnson',
    opp_full_name: 'Michael Johnson',
    opp_firm: 'Johnson Legal Group',
    opp_email: 'mjohnson@jlegal.com',
    opp_phone: '(555) 444-3333',
    opp_addr1: '200 Legal Plaza',
    opp_addr2: 'Floor 8',
    opp_city: 'Santa Monica',
    opp_state: 'CA',
    opp_zip: '90401',
  };
  return examples[fieldId] || 'Sample Value';
};

const GlobalFields = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('system-fields');
  const [systemFields, setSystemFields] = useState([]);
  const [systemLists, setSystemLists] = useState([]);
  const [userLists, setUserLists] = useState([]);
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [helpText, setHelpText] = useState({ fields: '', lists: '' });
  const [isCrmMode, setIsCrmMode] = useState(false);
  
  // New list form state
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newList, setNewList] = useState({ name: '', description: '', options: '' });
  const [saving, setSaving] = useState(false);
  
  // Edit state
  const [editingList, setEditingList] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', options: '' });
  
  // Copy feedback
  const [copiedField, setCopiedField] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch user data
      const userRes = await fetch(`${API}/auth/me`, { credentials: 'include', headers });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
        setIsCrmMode(userData.app_mode === 'crm');
      }

      const [fieldsRes, listsRes] = await Promise.all([
        fetch(`${API}/global-fields/system`, { credentials: 'include', headers }),
        fetch(`${API}/global-fields/option-lists`, { credentials: 'include', headers })
      ]);

      if (fieldsRes.ok) {
        const fieldsData = await fieldsRes.json();
        setSystemFields(fieldsData.fields || []);
        setUserTimezone(fieldsData.user_timezone || 'UTC');
        setHelpText(prev => ({ ...prev, fields: fieldsData.help_text }));
      }

      if (listsRes.ok) {
        const listsData = await listsRes.json();
        setSystemLists(listsData.system_lists || []);
        setUserLists(listsData.user_lists || []);
        setHelpText(prev => ({ ...prev, lists: listsData.help_text }));
      }
    } catch (error) {
      toast.error('Failed to load global fields');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCreateList = async () => {
    if (!newList.name.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    
    const options = newList.options.split(',').map(o => o.trim()).filter(o => o);
    if (options.length === 0) {
      toast.error('Please enter at least one option');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/global-fields/option-lists`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newList.name.trim(),
          description: newList.description.trim() || null,
          options
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create list');
      }

      toast.success('Option list created!');
      setNewList({ name: '', description: '', options: '' });
      setShowNewListForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateList = async () => {
    if (!editForm.name.trim()) {
      toast.error('Please enter a list name');
      return;
    }

    const options = editForm.options.split(',').map(o => o.trim()).filter(o => o);
    if (options.length === 0) {
      toast.error('Please enter at least one option');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/global-fields/option-lists/${editingList}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          options
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update list');
      }

      toast.success('Option list updated!');
      setEditingList(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!confirm('Are you sure you want to delete this option list?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/global-fields/option-lists/${listId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete list');
      
      toast.success('Option list deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const startEditing = (list) => {
    setEditingList(list.list_id);
    setEditForm({
      name: list.name,
      description: list.description || '',
      options: list.options.join(', ')
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Global Fields</h1>
          <p className="text-slate-500 mt-1">Manage system fields and reusable option lists</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 mb-6 bg-slate-100 rounded-xl overflow-x-auto">
          <button
            onClick={() => setActiveTab('system-fields')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'system-fields'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-system-fields"
          >
            <Calendar className="w-4 h-4" />
            System Fields
          </button>
          <button
            onClick={() => setActiveTab('option-lists')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'option-lists'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-option-lists"
          >
            <List className="w-4 h-4" />
            Option Lists
          </button>
          {/* Case-Specific Field Tabs (CRM Mode Only) */}
          {isCrmMode && Object.entries(CASE_SPECIFIC_FIELDS).map(([key, category]) => {
            const Icon = category.icon;
            const colorClasses = {
              blue: activeTab === key ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:text-blue-600',
              green: activeTab === key ? 'bg-green-50 text-green-700' : 'text-slate-600 hover:text-green-600',
              purple: activeTab === key ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:text-purple-600',
              orange: activeTab === key ? 'bg-orange-50 text-orange-700' : 'text-slate-600 hover:text-orange-600',
              indigo: activeTab === key ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:text-indigo-600',
              red: activeTab === key ? 'bg-red-50 text-red-700' : 'text-slate-600 hover:text-red-600',
            };
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === key
                    ? `bg-white shadow-sm ${colorClasses[category.color]}`
                    : colorClasses[category.color]
                }`}
                data-testid={`tab-${key}`}
              >
                <Icon className="w-4 h-4" />
                {category.title.replace(' Fields', '')}
              </button>
            );
          })}
        </div>

        {/* System Fields Tab */}
        {activeTab === 'system-fields' && (
          <div>
            {/* Help Banner */}
            <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-100 flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-amber-800">
                  <strong>How to use:</strong> {helpText.fields || 'Add these placeholders to your Word document. They will be automatically replaced with the current date when you export.'}
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-amber-700">
                  <Globe className="w-3.5 h-3.5" />
                  <span>Your timezone: <strong>{userTimezone}</strong></span>
                  <Link to="/settings" className="inline-flex items-center gap-1 hover:underline">
                    <Settings className="w-3 h-3" />
                    Change in Settings
                  </Link>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {systemFields.map((field) => (
                <div 
                  key={field.field_id}
                  className="p-5 rounded-xl bg-white border border-slate-200 hover:border-amber-200 transition-colors"
                  data-testid={`system-field-${field.field_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{field.label}</span>
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Auto
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-4">{field.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">Placeholder:</span>
                          <code className="px-3 py-1.5 rounded-lg bg-slate-100 text-violet-600 text-sm font-mono">
                            {field.placeholder}
                          </code>
                          <button
                            onClick={() => copyToClipboard(field.placeholder, field.field_id)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                            title="Copy placeholder"
                          >
                            {copiedField === field.field_id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">Preview:</span>
                          <span className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-sm font-medium">
                            {field.current_value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option Lists Tab */}
        {activeTab === 'option-lists' && (
          <div>
            {/* Header with Create Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 rounded-xl bg-violet-50 border border-violet-100 flex items-start gap-3 flex-1 mr-4">
                <HelpCircle className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-violet-800">
                  <strong>Quick Tip:</strong> {helpText.lists || 'Create reusable lists to quickly populate options for dropdown, radio, and checkbox fields in your templates.'}
                </div>
              </div>
              <Button
                onClick={() => setShowNewListForm(true)}
                className="h-10 px-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex-shrink-0"
                data-testid="create-list-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create List
              </Button>
            </div>

            {/* New List Form */}
            {showNewListForm && (
              <div className="mb-6 p-6 rounded-xl bg-white border-2 border-violet-200 shadow-sm" data-testid="new-list-form">
                <h3 className="font-semibold text-slate-900 mb-4">Create New Option List</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-600 mb-1.5 block">List Name *</Label>
                    <Input
                      value={newList.name}
                      onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                      placeholder="e.g., Countries, Departments, Status Types"
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 mb-1.5 block">Description (Optional)</Label>
                    <Input
                      value={newList.description}
                      onChange={(e) => setNewList({ ...newList, description: e.target.value })}
                      placeholder="Brief description of what this list is for"
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 mb-1.5 block">Options * (comma-separated)</Label>
                    <Textarea
                      value={newList.options}
                      onChange={(e) => setNewList({ ...newList, options: e.target.value })}
                      placeholder="Option 1, Option 2, Option 3"
                      rows={3}
                      className="rounded-xl"
                    />
                    <p className="text-xs text-slate-400 mt-1.5">
                      Enter options separated by commas. Example: Red, Green, Blue
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleCreateList}
                      disabled={saving}
                      className="h-10 px-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      {saving ? 'Creating...' : 'Create List'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setShowNewListForm(false); setNewList({ name: '', description: '', options: '' }); }}
                      className="h-10 px-5 rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* System Lists */}
            {systemLists.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  System Lists (Read-only)
                </h3>
                <div className="grid gap-4">
                  {systemLists.map((list) => (
                    <div 
                      key={list.list_id}
                      className="p-5 rounded-xl bg-slate-50 border border-slate-200"
                      data-testid={`system-list-${list.list_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-semibold text-slate-700">{list.name}</span>
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs">
                            {list.options.length} options
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-xs flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          System
                        </span>
                      </div>
                      {list.description && (
                        <p className="text-sm text-slate-500 mb-3">{list.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {list.options.map((option, idx) => (
                          <span key={idx} className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs">
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Lists */}
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                <Edit2 className="w-4 h-4" />
                Your Custom Lists
              </h3>
              {userLists.length === 0 ? (
                <div className="p-10 rounded-xl bg-white border-2 border-dashed border-slate-200 text-center">
                  <List className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-1 font-medium">No custom lists yet</p>
                  <p className="text-sm text-slate-400">Create your first option list to quickly populate field options</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {userLists.map((list) => (
                    <div 
                      key={list.list_id}
                      className="p-5 rounded-xl bg-white border border-slate-200 hover:border-violet-200 transition-colors"
                      data-testid={`user-list-${list.list_id}`}
                    >
                      {editingList === list.list_id ? (
                        /* Edit Form */
                        <div className="space-y-3">
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="List name"
                            className="h-10 rounded-xl"
                          />
                          <Input
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Description (optional)"
                            className="h-10 rounded-xl"
                          />
                          <Textarea
                            value={editForm.options}
                            onChange={(e) => setEditForm({ ...editForm, options: e.target.value })}
                            placeholder="Options (comma-separated)"
                            rows={2}
                            className="rounded-xl"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={handleUpdateList}
                              disabled={saving}
                              size="sm"
                              className="h-9 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white"
                            >
                              <Save className="w-3.5 h-3.5 mr-1.5" />
                              {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingList(null)}
                              className="h-9 px-4 rounded-lg"
                            >
                              <X className="w-3.5 h-3.5 mr-1.5" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* Display Mode */
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-semibold text-slate-900">{list.name}</span>
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 text-xs">
                                {list.options.length} options
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => startEditing(list)}
                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                                title="Edit list"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteList(list.list_id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                title="Delete list"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {list.description && (
                            <p className="text-sm text-slate-500 mb-3">{list.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {list.options.map((option, idx) => (
                              <span key={idx} className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 text-xs">
                                {option}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Case-Specific Field Tabs Content (CRM Mode Only) */}
        {isCrmMode && Object.entries(CASE_SPECIFIC_FIELDS).map(([key, category]) => {
          if (activeTab !== key) return null;
          
          const Icon = category.icon;
          const colorConfig = {
            blue: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-800', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', code: 'text-blue-600', preview: 'bg-blue-50 text-blue-700' },
            green: { bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-800', icon: 'text-green-600', badge: 'bg-green-100 text-green-700', code: 'text-green-600', preview: 'bg-green-50 text-green-700' },
            purple: { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-800', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700', code: 'text-purple-600', preview: 'bg-purple-50 text-purple-700' },
            orange: { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-800', icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-700', code: 'text-orange-600', preview: 'bg-orange-50 text-orange-700' },
            indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-800', icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-700', code: 'text-indigo-600', preview: 'bg-indigo-50 text-indigo-700' },
            red: { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-800', icon: 'text-red-600', badge: 'bg-red-100 text-red-700', code: 'text-red-600', preview: 'bg-red-50 text-red-700' },
          };
          const colors = colorConfig[category.color];
          
          return (
            <div key={key}>
              {/* Help Banner */}
              <div className={`mb-6 p-4 rounded-xl ${colors.bg} ${colors.border} border flex items-start gap-3`}>
                <Icon className={`w-5 h-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
                <div className="flex-1">
                  <div className={`text-sm ${colors.text}`}>
                    <strong>How to use:</strong> {category.description}. Copy placeholders and paste them into your Word document template. When running from a case, these will be auto-filled.
                  </div>
                  <div className={`mt-2 text-xs ${colors.text} opacity-75`}>
                    <strong>Tip:</strong> Enable &quot;Case-Specific Template&quot; in the Template Editor to map fields directly to these placeholders.
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                {category.fields.map((field) => (
                  <div 
                    key={field.field_id}
                    className={`p-5 rounded-xl bg-white border border-slate-200 hover:${colors.border} transition-colors`}
                    data-testid={`case-field-${field.field_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">{field.label}</span>
                          <span className={`px-2 py-0.5 rounded-full ${colors.badge} text-xs font-medium flex items-center gap-1`}>
                            <Icon className="w-3 h-3" />
                            {category.title.replace(' Fields', '')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mb-4">{field.description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">Placeholder:</span>
                            <code className={`px-3 py-1.5 rounded-lg bg-slate-100 ${colors.code} text-sm font-mono`}>
                              {field.placeholder}
                            </code>
                            <button
                              onClick={() => copyToClipboard(field.placeholder, field.field_id)}
                              className={`p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:${colors.icon} transition-colors`}
                              title="Copy placeholder"
                            >
                              {copiedField === field.field_id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">Example:</span>
                            <span className={`px-3 py-1.5 rounded-lg ${colors.preview} text-sm font-medium`}>
                              {getExampleValue(field.field_id)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalFields;
